/*
 * <yugal-studio>: the portrait studio on style pages.
 * Photographs → phone or email verification → portrait → preview → add to cart.
 * Talks to the Yugal API (worker/src/routes/studio.ts). Photographs stay in the browser until
 * the phone is verified, then upload straight to private storage over presigned URLs.
 */
(() => {
  const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_EDGE = 2400; // longest side after client-side resize
  const MIN_SIDE = 600; // shortest side we accept
  const JPEG_QUALITY = 0.9;
  const POLL_MS = 3000;
  const FACT_MS = 8000;
  const GIVE_UP_MS = 10 * 60 * 1000;
  const TOKEN_KEY = 'yugal:studio:token';
  const RUN_KEY = (slug) => `yugal:studio:run:${slug}`;
  const SITTING_KEY = 'yugal:studio:sitting';
  const SITTING_MAX = 3;

  /**
   * Hangs an image as a canvas on a wall (same tree and classes as snippets/yugal-canvas.liquid,
   * styled in yugal.css). Spans, so it is valid inside buttons.
   */
  const canvasMockup = (img, size = '') => {
    const root = document.createElement('span');
    root.className = `y-canvas y-canvas--portrait${size ? ` y-canvas--${size}` : ''}`;
    const wall = document.createElement('span');
    wall.className = 'y-canvas__wall';
    const art = document.createElement('span');
    art.className = 'y-canvas__art';
    art.append(img);
    wall.append(art);
    root.append(wall);
    return root;
  };

  const store = {
    get(key) {
      try {
        return JSON.parse(localStorage.getItem(key));
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {}
    },
    del(key) {
      try {
        localStorage.removeItem(key);
      } catch {}
    },
  };

  class ApiError extends Error {
    constructor(code, status, data = {}) {
      super(code);
      this.code = code;
      this.status = status;
      this.data = data;
    }
  }

  const sleep = (ms, signal) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    });

  class YugalStudio extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.slug = this.dataset.style;
      this.styleName = this.dataset.styleName;
      this.variantId = Number(this.dataset.variantId);
      this.cartUrl = this.dataset.cartUrl || '/cart';
      // Photo-first sitting (a page, not a style): one upload, then several traditions.
      this.sitting = this.dataset.sitting === 'true';
      this.styles = this.sitting ? JSON.parse(this.querySelector('[data-styles]')?.textContent || '[]') : [];
      this.siteKey = this.dataset.turnstileSiteKey || '';
      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');

      this.abort = new AbortController();
      this.state = {
        mode: 'together',
        files: {}, // kind -> { blob, url }
        token: store.get(TOKEN_KEY)?.token ?? null,
        via: 'phone',
        target: null, // { phone } | { email } the code was sent to
        channels: null,
        sessionId: null,
        generationId: null,
        variants: [], // [{ variant, preview_url }]
        variant: 'primary',
        voice: null, // { blob, url, audioId }
        freeLeft: null,
        busy: false,
      };

      this.$ = (selector) => this.querySelector(selector);
      this.step = this.$('[data-step="photos"]') ? 'photos' : null;
      this.bind();
      this.renderDrops();
      this.setStep('photos', { focus: false });
      this.restore().catch(() => {});
    }

    disconnectedCallback() {
      this.abort.abort();
      this.stopRecording(true);
      if (this.state.voice) URL.revokeObjectURL(this.state.voice.url);
      for (const file of Object.values(this.state.files)) URL.revokeObjectURL(file.url);
    }

    // ------------------------------------------------------------------ text

    t(key, vars = {}) {
      const text = this.strings[key] ?? key;
      return text.replace(/\{(\w+)\}/g, (_, name) => (name in vars ? vars[name] : `{${name}}`));
    }

    error(err) {
      if (err?.name === 'AbortError') return '';
      if (err instanceof ApiError) return this.strings[`errors.${err.code}`] || err.data?.message || this.t('errors.generic');
      if (err instanceof TypeError) return this.t('errors.network');
      return this.strings[`errors.${err?.message}`] || this.t('errors.generic');
    }

    status(message = '', kind = 'info') {
      const el = this.$('[data-status]');
      el.textContent = message;
      el.dataset.kind = kind;
    }

    // ------------------------------------------------------------------ events

    bind() {
      this.querySelectorAll('[data-mode]').forEach((button) =>
        button.addEventListener('click', () => this.setMode(button.dataset.mode)),
      );
      this.$('[data-action="continue"]').addEventListener('click', () => this.onContinue());
      this.$('[data-action="send-code"]').addEventListener('click', () => this.sendCode());
      this.$('[data-action="resend"]').addEventListener('click', () => this.sendCode());
      this.$('[data-action="verify"]').addEventListener('click', () => this.verifyCode());
      this.$('[data-action="retry"]').addEventListener('click', () => this.generate());
      this.$('[data-action="reset"]').addEventListener('click', () => this.reset());
      this.$('[data-action="paint-styles"]')?.addEventListener('click', () => this.paintStyles());
      this.$('[data-action="save-lead"]').addEventListener('click', () => this.saveLead());
      this.$('[data-action="add-to-cart"]').addEventListener('click', () => this.addToCart());
      const submitOnEnter = (event) => event.key === 'Enter' && (event.preventDefault(), this.sendCode());
      this.$('[data-phone]').addEventListener('keydown', submitOnEnter);
      this.$('[data-email]').addEventListener('keydown', submitOnEnter);
      this.querySelectorAll('[data-via]').forEach((button) => button.addEventListener('click', () => this.setVia(button.dataset.via)));
      this.$('[data-code]').addEventListener('keydown', (event) => event.key === 'Enter' && (event.preventDefault(), this.verifyCode()));
      this.$('[data-preview-image]').addEventListener('error', () => this.refreshPreview());
      this.$('[data-action="voice-record"]').addEventListener('click', () => (this.recorder ? this.stopRecording() : this.startRecording()));
      this.$('[data-action="voice-remove"]').addEventListener('click', () => this.removeVoice());
      this.$('[data-voice]').hidden = !(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
    }

    setMode(mode) {
      if (this.state.busy || mode === this.state.mode) return;
      for (const file of Object.values(this.state.files)) URL.revokeObjectURL(file.url);
      this.state.files = {};
      this.state.mode = mode;
      this.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === mode)));
      this.renderDrops();
      this.status();
    }

    setStep(step, { focus = true } = {}) {
      this.step = step;
      this.querySelectorAll('[data-step]').forEach((section) => {
        section.hidden = section.dataset.step !== step;
      });
      const order = { photos: 0, phone: 1, lead: 1, styles: 2, working: 2, preview: 2 };
      this.querySelectorAll('[data-crumb]').forEach((crumb, index) => {
        crumb.toggleAttribute('data-current', index === order[step]);
        crumb.toggleAttribute('data-done', index < order[step]);
        if (index === order[step]) crumb.setAttribute('aria-current', 'step');
        else crumb.removeAttribute('aria-current');
      });
      // The one status line follows the customer, so a message sits next to what it is about.
      const slot = this.$(`[data-step="${step}"] [data-status-slot]`);
      const status = this.$('[data-status]');
      if (slot && status && status.parentElement !== slot) slot.append(status);
      if (focus) this.$(`[data-step="${step}"] [data-focus]`)?.focus({ preventScroll: false });
    }

    busy(on) {
      this.state.busy = on;
      this.toggleAttribute('aria-busy', on);
      this.querySelectorAll('button[data-action], [data-mode]').forEach((b) => {
        if (on) b.setAttribute('disabled', '');
        else b.removeAttribute('disabled');
      });
    }

    // ------------------------------------------------------------------ photographs

    kinds() {
      return this.state.mode === 'together' ? ['couple'] : ['person_a', 'person_b'];
    }

    renderDrops() {
      const host = this.$('[data-drops]');
      const template = this.$('template[data-drop]');
      host.replaceChildren();
      host.dataset.count = String(this.kinds().length);
      this.$('[data-photos-title]').textContent = this.t(this.state.mode === 'together' ? 'photos_title' : 'photos_title_separate');
      for (const kind of this.kinds()) {
        const node = template.content.firstElementChild.cloneNode(true);
        node.dataset.kind = kind;
        node.querySelector('[data-drop-label]').textContent = this.t(`drop_${kind}`);
        const input = node.querySelector('input[type="file"]');
        input.addEventListener('change', () => input.files[0] && this.pick(kind, input.files[0], node));
        for (const type of ['dragenter', 'dragover']) {
          node.addEventListener(type, (event) => (event.preventDefault(), node.setAttribute('data-over', '')));
        }
        for (const type of ['dragleave', 'drop']) node.addEventListener(type, () => node.removeAttribute('data-over'));
        node.addEventListener('drop', (event) => {
          event.preventDefault();
          const file = event.dataTransfer?.files?.[0];
          if (file) this.pick(kind, file, node);
        });
        host.append(node);
      }
    }

    async pick(kind, file, node) {
      this.status();
      try {
        const prepared = await this.prepareImage(file);
        const previous = this.state.files[kind];
        if (previous) URL.revokeObjectURL(previous.url);
        const url = URL.createObjectURL(prepared.blob);
        this.state.files[kind] = { blob: prepared.blob, url };
        node.dataset.filled = '';
        node.querySelector('[data-drop-image]').src = url;
        node.querySelector('[data-drop-action]').textContent = this.t('replace');
      } catch (err) {
        this.status(this.error(err), 'error');
      }
    }

    /** Validates, orients, downsizes and re-encodes as JPEG (which also strips EXIF/location). */
    async prepareImage(file) {
      if (!ACCEPT.includes(file.type)) throw new Error('unsupported_type');
      let bitmap;
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch {
        throw new Error('unreadable');
      }
      const { width, height } = bitmap;
      if (Math.min(width, height) < MIN_SIDE) {
        bitmap.close?.();
        throw new Error('too_small');
      }
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close?.();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
      if (!blob) throw new Error('unreadable');
      return { blob, width: canvas.width, height: canvas.height };
    }

    async onContinue() {
      const missing = this.kinds().some((kind) => !this.state.files[kind]);
      if (missing) return this.status(this.t('errors.need_photos'), 'error');
      if (!this.$('[data-consent]').checked) return this.status(this.t('errors.need_consent'), 'error');
      this.status();
      this.$('[data-photo-help]').hidden = true;

      if (this.state.token) {
        try {
          this.busy(true);
          const me = await this.request('/studio/me');
          this.state.freeLeft = me.free_generations_left;
          this.busy(false);
          // Say so before uploading anything.
          if (me.free_generations_left === 0) return this.status(this.t('errors.quota_exhausted'), 'error');
          return this.start();
        } catch (err) {
          this.busy(false);
          if (!(err instanceof ApiError && err.status === 401)) return this.status(this.error(err), 'error');
          this.forgetToken();
        }
      }
      this.setStep('phone');
      this.mountTurnstile();
      this.loadChannels();
    }

    // ------------------------------------------------------------------ phone

    mountTurnstile() {
      const host = this.$('[data-turnstile]');
      if (!this.siteKey || host.dataset.mounted) return;
      host.dataset.mounted = '';
      const render = () =>
        (this.widgetId = window.turnstile.render(host, {
          sitekey: this.siteKey,
          callback: (token) => (this.turnstileToken = token),
          'expired-callback': () => (this.turnstileToken = null),
          theme: 'light',
        }));
      if (window.turnstile) return render();
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = render;
      document.head.append(script);
    }

    /** Shows only the ways of verifying that can actually reach a customer right now. */
    async loadChannels() {
      if (this.state.channels) return;
      let channels = { phone: true, email: true };
      try {
        channels = await this.request('/studio/otp/channels', { auth: false });
      } catch {} // the server still decides on send; do not hide the form because of a hiccup
      this.state.channels = channels;
      const both = channels.phone && channels.email;
      this.$('[data-via-toggle]').hidden = !both;
      this.$('[data-verify-form]').hidden = !channels.phone && !channels.email;
      this.$('[data-unavailable]').hidden = channels.phone || channels.email;
      this.setVia(channels.phone ? 'phone' : 'email', { force: true });
    }

    setVia(via, { force = false } = {}) {
      if (!force && (this.state.busy || via === this.state.via)) return;
      this.state.via = via;
      this.querySelectorAll('[data-via]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.via === via)));
      this.querySelectorAll('[data-via-panel]').forEach((panel) => (panel.hidden = panel.dataset.viaPanel !== via));
      this.$('[data-whatsapp-row]').hidden = via !== 'phone';
      this.$('[data-code-row]').hidden = true;
      this.$('[data-phone-note]').textContent = '';
      this.$('[data-action="send-code"]').hidden = false;
      this.$('[data-action="resend"]').hidden = true;
      this.state.target = null;
      this.status();
    }

    digits() {
      let value = this.$('[data-phone]').value.replace(/\D/g, '');
      if (value.length > 10 && value.startsWith('91')) value = value.slice(2);
      if (value.length === 11 && value.startsWith('0')) value = value.slice(1);
      return value;
    }

    async sendCode() {
      let target;
      let shown;
      if (this.state.via === 'email') {
        const email = this.$('[data-email]').value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return this.status(this.t('errors.invalid_email'), 'error');
        target = { email };
        shown = email;
      } else {
        const digits = this.digits();
        if (!/^[6-9]\d{9}$/.test(digits)) return this.status(this.t('errors.invalid_phone'), 'error');
        target = { phone: `+91${digits}` };
        shown = `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
      }
      this.status();
      this.busy(true);
      try {
        await this.request('/studio/otp/send', {
          method: 'POST',
          auth: false,
          body: { ...target, turnstile_token: this.turnstileToken || null },
        });
        this.state.target = target;
        this.$('[data-code-row]').hidden = false;
        this.$('[data-phone-note]').textContent = this.t('code_sent', { to: shown });
        this.$('[data-action="send-code"]').hidden = true;
        this.$('[data-action="resend"]').hidden = false;
        this.$('[data-code]').focus();
      } catch (err) {
        this.status(this.error(err), 'error');
      } finally {
        if (this.widgetId !== undefined) window.turnstile?.reset(this.widgetId);
        this.turnstileToken = null;
        this.busy(false);
      }
    }

    async verifyCode() {
      const code = this.$('[data-code]').value.replace(/\D/g, '');
      if (code.length !== 6 || !this.state.target) return this.status(this.t('errors.invalid_code'), 'error');
      this.status();
      this.busy(true);
      try {
        const result = await this.request('/studio/otp/verify', {
          method: 'POST',
          auth: false,
          body: {
            ...this.state.target,
            code,
            whatsapp_opt_in: Boolean(this.state.target.phone && this.$('[data-whatsapp]').checked),
          },
        });
        this.state.token = result.token;
        this.state.freeLeft = result.customer.free_generations_left;
        store.set(TOKEN_KEY, { token: result.token, expires_at: result.expires_at });
        this.busy(false);
        await this.start();
      } catch (err) {
        this.busy(false);
        this.status(this.error(err), 'error');
      }
    }

    /**
     * Start over with new photographs. Verification is kept; previews already made still count
     * against the free ones, and stay reachable from the order email if ordered.
     */
    reset() {
      if (!window.confirm(this.t('reset_confirm'))) return;
      this.abort.abort();
      this.abort = new AbortController();
      this.stopFacts();
      this.stopRecording(true);
      this.removeVoice();
      for (const file of Object.values(this.state.files)) URL.revokeObjectURL(file.url);
      Object.assign(this.state, { files: {}, sessionId: null, generationId: null, variants: [], variant: 'primary', partial: false, partialShown: 0, draftShown: false, busy: false });
      this.$('[data-photo-help]').hidden = true;
      store.del(RUN_KEY(this.slug));
      if (this.sitting) {
        store.del(SITTING_KEY);
        this.state.runs = [];
        this.renderRuns();
      }
      const note = this.$('[data-gift-note]');
      if (note) note.value = '';
      this.querySelectorAll('[data-letter-greeting], [data-letter-signed]').forEach((input) => (input.value = ''));
      this.$('[data-preview-figure]').hidden = true;
      this.renderDrops();
      this.status();
      this.setStep('photos');
    }

    forgetToken() {
      this.state.token = null;
      store.del(TOKEN_KEY);
    }

    // ------------------------------------------------------------------ generation

    /** Creates the session, uploads the photographs, then asks for the first portrait. */
    async start() {
      this.setStep('working');
      this.working(this.t('working_upload'), 'upload');
      try {
        const session = await this.request('/studio/sessions', {
          method: 'POST',
          body: { mode: this.state.mode, style_slug: this.slug },
        });
        this.state.sessionId = session.session_id;
        for (const kind of session.upload_kinds) {
          const { blob } = this.state.files[kind];
          const slot = await this.request('/studio/uploads', {
            method: 'POST',
            body: { session_id: session.session_id, kind, content_type: 'image/jpeg', consent: true },
          });
          const put = await fetch(slot.upload_url, { method: slot.method, headers: slot.headers, body: blob, signal: this.abort.signal });
          if (!put.ok) throw new Error('generic');
        }
        // Find the faces first (no painting, no free preview spent). A portrait needs both faces:
        // a photo where one can't be found is refused, with help a tap away.
        // Capped at 8 s so it never holds up the portrait for long.
        const check = await Promise.race([
          this.request(`/studio/sessions/${session.session_id}/check-photos`, { method: 'POST' }).catch(() => ({ ok: true })),
          sleep(8000, this.abort.signal).then(() => ({ ok: true })),
        ]);
        if (check.ok === false) {
          this.stopFacts();
          this.setStep('photos', { focus: false });
          this.$('[data-photo-help]').hidden = false;
          return this.status(this.t('photo_check'), 'error');
        }
        // Asked once, before the first portrait: who it is for.
        const me = await this.request('/studio/me').catch(() => ({ lead_complete: true }));
        if (!me.lead_complete) return this.askLead(me);
        await this.afterLead();
      } catch (err) {
        this.fail(err);
      }
    }

    /** Requests (another) portrait for the current session and waits for it. */
    async generate() {
      this.state.partialShown = 0;
      this.state.draftShown = false;
      this.setStep('working');
      this.working(this.t('working_compose', { style: this.styleName }));
      try {
        const run = await this.request('/studio/generations', { method: 'POST', body: { session_id: this.state.sessionId } });
        this.state.generationId = run.generation_id;
        this.state.freeLeft = run.free_generations_left;
        store.set(RUN_KEY(this.slug), { generation_id: run.generation_id, session_id: this.state.sessionId });
        await this.poll();
      } catch (err) {
        this.fail(err);
      }
    }

    async poll() {
      const startedAt = Date.now();
      while (true) {
        const gen = await this.request(`/studio/generations/${this.state.generationId}`);
        this.state.freeLeft = gen.free_generations_left;
        if (gen.status === 'done') return this.showPreview(gen);
        if (gen.status === 'failed') throw new Error('generation_failed');
        // Show each version as soon as it exists; ordering waits for the full set.
        if (gen.status === 'running' && gen.variants?.length > (this.state.partialShown || 0)) {
          this.state.partialShown = gen.variants.length;
          this.showPreview(gen, { partial: true });
        } else if (gen.status === 'running' && !gen.variants?.length && gen.draft_url && !this.state.draftShown) {
          // The first draft: composition done, faces still being repainted in the style.
          this.state.draftShown = true;
          this.showPreview({ variants: [{ variant: 'primary', preview_url: gen.draft_url }] }, { partial: true, draft: true });
        }
        if (Date.now() - startedAt > GIVE_UP_MS) {
          this.working(this.t('working_slow'), 'slow');
          return;
        }
        await sleep(POLL_MS, this.abort.signal);
      }
    }

    /** After a reload: pick up a portrait that was in flight or finished. */
    async restore() {
      if (this.sitting) return this.restoreSitting();
      const run = store.get(RUN_KEY(this.slug));
      if (!run || !this.state.token) return;
      try {
        const gen = await this.request(`/studio/generations/${run.generation_id}`);
        this.state.sessionId = run.session_id;
        this.state.generationId = run.generation_id;
        this.state.freeLeft = gen.free_generations_left;
        if (gen.status === 'done') return this.showPreview(gen);
        if (gen.status === 'queued' || gen.status === 'running') {
          this.setStep('working', { focus: false });
          this.working(this.t('working_compose', { style: this.styleName }));
          await this.poll().catch((err) => this.fail(err));
          return;
        }
        store.del(RUN_KEY(this.slug));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) this.forgetToken();
        store.del(RUN_KEY(this.slug));
      }
    }

    /** phase: 'upload' (sending photographs), 'compose' (painting) or 'slow'; drives the stage list. */
    working(message, phase = 'compose') {
      this.$('[data-working-message]').textContent = message;
      const step = this.$('[data-step="working"]');
      if (step) step.dataset.phase = phase;
      this.startFacts();
    }

    /** Rotates short facts about the traditions while the portrait is being painted. */
    startFacts() {
      const box = this.$('[data-fact-box]');
      const text = this.$('[data-fact]');
      // Two strings: Shopify caps each translation at 1,000 characters.
      const facts = [this.strings.working_facts, this.strings.working_facts_2].filter(Boolean).join('|').split('|').map((f) => f.trim()).filter(Boolean);
      if (!box || !text || !facts.length || this.factTimer) return;
      let i = Math.floor(Math.random() * facts.length);
      const showFact = () => {
        text.setAttribute('data-fading', '');
        setTimeout(() => {
          text.textContent = facts[i % facts.length];
          i++;
          text.removeAttribute('data-fading');
        }, text.textContent ? 600 : 0);
      };
      box.hidden = false;
      showFact();
      this.factTimer = setInterval(showFact, FACT_MS);
    }

    stopFacts() {
      clearInterval(this.factTimer);
      this.factTimer = null;
    }

    fail(err) {
      const message = this.error(err);
      if (!message) return;
      if (err instanceof ApiError && err.status === 401) this.forgetToken();
      // Back to a step the customer can act on.
      const retryable = this.state.sessionId && !(err instanceof ApiError && ['quota_exhausted', 'daily_cap_reached'].includes(err.code));
      this.setStep(retryable ? 'preview' : 'photos', { focus: false });
      if (retryable) this.showRetryOnly();
      this.status(message, 'error');
    }

    // ------------------------------------------------------------------ preview

    /** partial: some versions are still being painted, so ordering waits for the full set. */
    showPreview(gen, { partial = false, draft = false } = {}) {
      this.stopFacts();
      const image = this.$('[data-preview-image]');
      image.removeAttribute('data-retried');
      const keep = this.state.variant;
      this.state.variants = gen.variants?.length ? gen.variants : [{ variant: 'primary', preview_url: gen.preview_url }];
      this.state.partial = partial;
      this.renderVersions();
      const chosen = this.state.variants.some((v) => v.variant === keep) ? keep : this.state.variants[0].variant;
      this.selectVersion(chosen, { focus: false });
      this.$('[data-preview-figure]').hidden = false;
      this.$('[data-preview-figure]').toggleAttribute('data-draft', draft);
      this.$('[data-cart-panel]').hidden = false;

      const note = this.$('[data-partial-note]');
      if (note) {
        note.hidden = !draft;
        note.textContent = draft ? this.t('versions_draft') : '';
      }
      // A finished version can be ordered straight away; only the dimmed draft can't.
      const cart = this.$('[data-action="add-to-cart"]');
      cart.disabled = draft;
      cart.textContent = draft ? this.t('versions_finishing') : this.t('add_to_cart');
      this.$('[data-action="retry"]').hidden = partial || !(this.state.freeLeft > 0);

      if (!partial) this.updateRetry();
      // In a sitting, each tradition is one portrait: another try means starting a new sitting.
      if (this.sitting) this.$('[data-action="retry"]').hidden = true;
      if (this.step !== 'preview') this.setStep('preview');
      if (!partial) this.status(gen.photo_warning ? this.t('photo_warning') : '');
    }

    /** Each preview has up to three versions; the customer orders the one they choose. */
    renderVersions() {
      const host = this.$('[data-versions]');
      host.replaceChildren();
      host.hidden = this.state.variants.length < 2;
      this.state.variants.forEach((v, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ys__version';
        button.setAttribute('role', 'radio');
        button.dataset.version = v.variant;
        button.setAttribute('aria-label', this.t('version_n', { n: index + 1 }));
        const img = document.createElement('img');
        img.src = v.preview_url;
        img.alt = '';
        img.width = 160;
        img.height = 240;
        img.loading = 'lazy';
        img.addEventListener('error', () => this.refreshPreview());
        button.append(canvasMockup(img, 'sm'));
        button.addEventListener('click', () => this.selectVersion(v.variant));
        button.addEventListener('keydown', (event) => this.versionKeys(event, index));
        host.append(button);
      });
    }

    versionKeys(event, index) {
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const list = this.state.variants;
      this.selectVersion(list[(index + step + list.length) % list.length].variant);
    }

    selectVersion(variant, { focus = true } = {}) {
      const chosen = this.state.variants.find((v) => v.variant === variant) || this.state.variants[0];
      this.state.variant = chosen.variant;
      this.$('[data-preview-image]').src = chosen.preview_url;
      // "See it on your wall" for this version (a signed 3D model link from the API).
      const ar = this.$('[data-ar-trigger]');
      if (ar) {
        ar.hidden = !chosen.ar_url;
        if (chosen.ar_url) ar.dataset.arSrc = chosen.ar_url;
        else delete ar.dataset.arSrc;
      }
      this.querySelectorAll('[data-version]').forEach((button) => {
        const on = button.dataset.version === chosen.variant;
        button.setAttribute('aria-checked', String(on));
        button.tabIndex = on ? 0 : -1;
        if (on && focus) button.focus();
      });
    }

    /** Preview URLs expire after ten minutes; fetch fresh ones once if an image fails to load. */
    async refreshPreview() {
      const image = this.$('[data-preview-image]');
      if (image.dataset.retried || !this.state.generationId) return;
      image.dataset.retried = '1';
      try {
        const gen = await this.request(`/studio/generations/${this.state.generationId}`);
        if (!gen.variants?.length && !gen.preview_url) return;
        const current = this.state.variant;
        this.state.variants = gen.variants?.length ? gen.variants : [{ variant: 'primary', preview_url: gen.preview_url }];
        this.renderVersions();
        this.selectVersion(current, { focus: false });
      } catch {}
    }

    updateRetry() {
      const left = this.state.freeLeft;
      const label = this.$('[data-previews-left]');
      label.textContent = left === 0 ? this.t('previews_none') : left === 1 ? this.t('previews_left_one') : this.t('previews_left', { count: left });
      this.$('[data-action="retry"]').hidden = !(left > 0);
    }

    showRetryOnly() {
      this.$('[data-preview-figure]').hidden = true;
      this.$('[data-cart-panel]').hidden = true;
      this.updateRetry();
    }

    // ------------------------------------------------------------------ who it is for

    /** Offers the contact they didn't verify with: email after a phone code, WhatsApp after an email one. */
    askLead(me) {
      this.stopFacts();
      const other = me.phone ? 'email' : 'phone';
      this.querySelectorAll('[data-lead-contact]').forEach((field) => (field.hidden = field.dataset.leadContact !== other));
      this.state.leadContact = other;
      this.status();
      this.setStep('lead');
    }

    async saveLead() {
      const body = {
        first_name: this.$('[data-lead-name]').value.trim(),
        gift_for: this.$('[data-lead-for]').value,
        occasion: this.$('[data-lead-occasion]').value,
        occasion_date: this.$('[data-lead-date]').value || undefined,
      };
      if (!body.first_name || !body.gift_for || !body.occasion) return this.status(this.t('lead_missing'), 'error');
      if (this.state.leadContact === 'email') body.contact_email = this.$('[data-lead-email]').value.trim() || undefined;
      else {
        body.contact_phone = this.$('[data-lead-phone]').value.trim() || undefined;
        body.whatsapp_opt_in = Boolean(body.contact_phone && this.$('[data-lead-whatsapp]').checked);
      }
      this.status();
      this.busy(true);
      try {
        await this.request('/studio/me', { method: 'POST', body });
        this.busy(false);
        await this.afterLead();
      } catch (err) {
        this.busy(false);
        const field = err instanceof ApiError ? err.data?.field : null;
        if (field === 'contact_email' || field === 'contact_phone' || field === 'occasion_date') return this.status(this.t('lead_contact_invalid'), 'error');
        this.status(this.error(err), 'error');
      }
    }

    async afterLead() {
      if (this.sitting) return this.chooseStyles();
      try {
        await this.generate();
      } catch (err) {
        this.fail(err);
      }
    }

    // ------------------------------------------------------------------ sitting

    /** After the photographs: choose up to three traditions (no more than the free previews left). */
    chooseStyles() {
      this.stopFacts();
      const limit = Math.max(1, Math.min(SITTING_MAX, this.state.freeLeft ?? SITTING_MAX));
      this.state.styleLimit = limit;
      this.state.chosenStyles = this.state.chosenStyles?.filter((s) => this.styles.some((x) => x.slug === s)).slice(0, limit) || [];
      const grid = this.$('[data-style-grid]');
      let group = null;
      grid.replaceChildren(
        ...this.styles.flatMap((style) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ys__style';
          button.dataset.slug = style.slug;
          button.setAttribute('aria-pressed', String(this.state.chosenStyles.includes(style.slug)));
          if (style.image) {
            const img = document.createElement('img');
            img.src = style.image;
            img.alt = '';
            img.width = 180;
            img.height = 270;
            img.loading = 'lazy';
            button.append(img);
          }
          const name = document.createElement('span');
          name.className = 'ys__style-name';
          name.textContent = style.name;
          button.append(name);
          if (style.region) {
            const region = document.createElement('span');
            region.className = 'ys__style-region';
            region.textContent = style.region;
            button.append(region);
          }
          button.addEventListener('click', () => this.toggleStyle(style.slug));
          // A heading where the list moves from the painted traditions to the modern looks.
          if (style.group && style.group !== group && this.styles.some((x) => x.group !== style.group)) {
            group = style.group;
            const heading = document.createElement('p');
            heading.className = 'y-eyebrow ys__style-group';
            heading.textContent = this.t(group === 'modern' ? 'styles_group_modern' : 'styles_group_classic');
            return [heading, button];
          }
          return [button];
        }),
      );
      this.updateStyleChoice();
      this.setStep('styles');
    }

    toggleStyle(slug) {
      const chosen = this.state.chosenStyles;
      const at = chosen.indexOf(slug);
      if (at >= 0) chosen.splice(at, 1);
      else if (chosen.length < this.state.styleLimit) chosen.push(slug);
      else return this.status(this.t('styles_limit', { count: this.state.styleLimit }), 'error');
      this.status();
      this.updateStyleChoice();
    }

    updateStyleChoice() {
      const chosen = this.state.chosenStyles;
      const limit = this.state.styleLimit;
      this.querySelectorAll('[data-style-grid] [data-slug]').forEach((b) => b.setAttribute('aria-pressed', String(chosen.includes(b.dataset.slug))));
      this.$('[data-styles-hint]').textContent = this.t(limit === 1 ? 'styles_hint_one' : 'styles_hint', { count: limit, chosen: chosen.length });
      this.$('[data-action="paint-styles"]').disabled = chosen.length === 0;
    }

    /** One session per tradition (copies of the same photographs), one portrait each. */
    async paintStyles() {
      const chosen = [...this.state.chosenStyles];
      if (!chosen.length) return;
      this.setStep('working');
      this.working(this.t('working_compose_many', { count: chosen.length }));
      const baseSlug = this.slug;
      const runs = [];
      try {
        for (const slug of chosen) {
          const style = this.styles.find((s) => s.slug === slug);
          let sessionId = this.state.sessionId;
          if (slug !== baseSlug) {
            const fork = await this.request(`/studio/sessions/${this.state.sessionId}/fork`, { method: 'POST', body: { style_slug: slug } });
            sessionId = fork.session_id;
          }
          const run = await this.request('/studio/generations', { method: 'POST', body: { session_id: sessionId } });
          this.state.freeLeft = run.free_generations_left;
          runs.push({ slug, name: style.name, variantId: style.variant_id, sessionId, generationId: run.generation_id, gen: null });
        }
      } catch (err) {
        // Whatever was started still counts; show those, and say why the rest didn't start.
        if (!runs.length) return this.fail(err);
        this.state.sittingError = this.error(err);
      }
      this.state.runs = runs;
      this.state.userPicked = false;
      store.set(SITTING_KEY, { runs: runs.map(({ gen, ...r }) => r) });
      this.activateRun(0, { quiet: true });
      await this.pollSitting().catch((err) => this.fail(err));
    }

    async restoreSitting() {
      const saved = store.get(SITTING_KEY);
      if (!saved?.runs?.length || !this.state.token) return;
      this.state.runs = saved.runs.map((r) => ({ ...r, gen: null }));
      this.activateRun(0, { quiet: true });
      this.setStep('working', { focus: false });
      this.working(this.t('working_compose_many', { count: this.state.runs.length }));
      await this.pollSitting().catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          this.forgetToken();
          store.del(SITTING_KEY);
          this.setStep('photos', { focus: false });
          return;
        }
        this.fail(err);
      });
    }

    /** Polls every unfinished tradition; shows the active one, and the first to have a picture. */
    async pollSitting() {
      const startedAt = Date.now();
      while (true) {
        let pending = 0;
        for (const run of this.state.runs) {
          if (run.gen && ['done', 'failed'].includes(run.gen.status)) continue;
          try {
            run.gen = await this.request(`/studio/generations/${run.generationId}`);
            this.state.freeLeft = run.gen.free_generations_left;
          } catch (err) {
            if (err?.name === 'AbortError' || (err instanceof ApiError && err.status === 401)) throw err;
            if (err instanceof ApiError && err.status === 404) run.gen = { status: 'failed' };
          }
          if (!run.gen || !['done', 'failed'].includes(run.gen.status)) pending++;
        }
        const active = this.state.runs[this.state.activeRun];
        // Nothing to see yet for the active tradition: switch to the first that has a picture.
        if (!this.hasPicture(active) && !this.state.userPicked) {
          const ready = this.state.runs.findIndex((r) => this.hasPicture(r));
          if (ready >= 0) this.state.activeRun = ready;
        }
        this.renderRuns();
        this.showRun();
        if (!pending) {
          if (this.state.sittingError) this.status(this.state.sittingError, 'error');
          return;
        }
        if (Date.now() - startedAt > GIVE_UP_MS) {
          if (this.step === 'working') this.working(this.t('working_slow'), 'slow');
          return;
        }
        await sleep(POLL_MS, this.abort.signal);
      }
    }

    hasPicture(run) {
      return Boolean(run?.gen && (run.gen.variants?.length || run.gen.draft_url));
    }

    /** Makes a tradition the current one: cart, versions and wall view follow it. */
    activateRun(index, { quiet = false } = {}) {
      const run = this.state.runs[index];
      if (!run) return;
      this.state.activeRun = index;
      this.slug = run.slug;
      this.styleName = run.name;
      this.variantId = Number(run.variantId);
      this.state.sessionId = run.sessionId;
      this.state.generationId = run.generationId;
      this.state.variant = 'primary';
      this.state.lastShown = null;
      if (!quiet) {
        this.state.userPicked = true;
        this.renderRuns();
        this.showRun();
      }
    }

    showRun() {
      const run = this.state.runs[this.state.activeRun];
      if (!run) return;
      const gen = run.gen;
      // Avoid redrawing (and losing the chosen version) when nothing changed.
      const signature = gen ? `${run.generationId}:${gen.status}:${gen.variants?.length || 0}:${gen.draft_url ? 1 : 0}` : `${run.generationId}:none`;
      if (signature === this.state.lastShown) return;
      this.state.lastShown = signature;
      if (gen?.variants?.length) return this.showPreview(gen, { partial: gen.status !== 'done' });
      if (gen?.draft_url && gen.status === 'running') {
        return this.showPreview({ variants: [{ variant: 'primary', preview_url: gen.draft_url }] }, { partial: true, draft: true });
      }
      if (!this.state.runs.some((r) => this.hasPicture(r))) return; // still on the working screen
      // This tradition has nothing yet (or failed) while another does: say so in its place.
      this.$('[data-preview-figure]').hidden = true;
      this.$('[data-cart-panel]').hidden = true;
      if (this.step !== 'preview') this.setStep('preview');
      this.status(gen?.status === 'failed' ? this.t('styles_failed', { style: run.name }) : this.t('styles_painting', { style: run.name }));
    }

    renderRuns() {
      const host = this.$('[data-runs]');
      if (!host) return;
      const runs = this.state.runs || [];
      host.hidden = runs.length < 2;
      host.replaceChildren(
        ...runs.map((run, index) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'ys__run';
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', String(index === this.state.activeRun));
          const name = document.createElement('span');
          name.className = 'ys__run-name';
          name.textContent = run.name;
          const state = document.createElement('span');
          state.className = 'ys__run-state';
          const status = run.gen?.status;
          state.textContent = status === 'done' ? this.t('styles_ready') : status === 'failed' ? this.t('styles_failed_short') : run.gen?.variants?.length ? this.t('styles_partly') : this.t('styles_painting_short');
          tab.append(name, state);
          tab.addEventListener('click', () => this.activateRun(index));
          return tab;
        }),
      );
    }

    // ------------------------------------------------------------------ voice note

    static VOICE_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];
    static VOICE_MAX_SECONDS = 60;

    async startRecording() {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      } catch {
        return this.status(this.t('errors.voice_denied'), 'error');
      }
      this.removeVoice();
      // Keep the tradition's music out of the recording (not remembered as the visitor's choice).
      document.querySelectorAll('yugal-music').forEach((el) => el.pause?.({ remember: false }));
      const mimeType = YugalStudio.VOICE_TYPES.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 64000 } : undefined);
      const chunks = [];
      recorder.addEventListener('dataavailable', (event) => event.data.size && chunks.push(event.data));
      recorder.addEventListener('stop', () => {
        stream.getTracks().forEach((track) => track.stop());
        if (recorder.discard || !chunks.length) return;
        const blob = new Blob(chunks, { type: (recorder.mimeType || mimeType || 'audio/webm').split(';')[0] });
        this.state.voice = { blob, url: URL.createObjectURL(blob), audioId: null };
        const playback = this.$('[data-voice-playback]');
        playback.src = this.state.voice.url;
        playback.hidden = false;
        this.$('[data-action="voice-remove"]').hidden = false;
      });
      this.recorder = recorder;
      // Resolves after the handler above has turned the chunks into this.state.voice.
      this.recorderStopped = new Promise((resolve) => recorder.addEventListener('stop', resolve, { once: true }));
      recorder.start(1000);
      this.status();

      const button = this.$('[data-action="voice-record"]');
      button.textContent = this.t('voice_stop');
      button.setAttribute('aria-pressed', 'true');
      const timer = this.$('[data-voice-timer]');
      timer.hidden = false;
      const started = Date.now();
      const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      const tick = () => {
        const elapsed = Math.floor((Date.now() - started) / 1000);
        timer.textContent = `${fmt(elapsed)} / ${fmt(YugalStudio.VOICE_MAX_SECONDS)}`;
        if (elapsed >= YugalStudio.VOICE_MAX_SECONDS) this.stopRecording();
      };
      tick();
      this.voiceTimer = setInterval(tick, 250);
    }

    stopRecording(discard = false) {
      clearInterval(this.voiceTimer);
      const recorder = this.recorder;
      this.recorder = null;
      if (recorder && recorder.state !== 'inactive') {
        recorder.discard = discard;
        recorder.stop();
      }
      if (discard) return;
      const button = this.$('[data-action="voice-record"]');
      button.textContent = this.t('voice_again');
      button.setAttribute('aria-pressed', 'false');
      this.$('[data-voice-timer]').hidden = true;
    }

    removeVoice() {
      if (this.state.voice) URL.revokeObjectURL(this.state.voice.url);
      this.state.voice = null;
      const playback = this.$('[data-voice-playback]');
      playback.removeAttribute('src');
      playback.hidden = true;
      this.$('[data-action="voice-remove"]').hidden = true;
      this.$('[data-action="voice-record"]').textContent = this.t('voice_record');
    }

    /** Uploads the recorded note once (presigned PUT) and returns its id. */
    async uploadVoice() {
      const voice = this.state.voice;
      if (!voice) return null;
      if (voice.audioId) return voice.audioId;
      const slot = await this.request('/studio/audio', { method: 'POST', body: { content_type: voice.blob.type } });
      if (voice.blob.size > slot.max_bytes) throw new Error('voice_upload');
      const put = await fetch(slot.upload_url, { method: 'PUT', headers: slot.headers, body: voice.blob, signal: this.abort.signal });
      if (!put.ok) throw new Error('voice_upload');
      voice.audioId = slot.audio_id;
      return voice.audioId;
    }

    async addToCart() {
      if (!this.state.generationId || !this.variantId) return;
      const note = this.$('[data-gift-note]').value.trim().slice(0, 500);
      const properties = {
        Style: this.styleName,
        _style: this.slug,
        _generation_id: this.state.generationId,
        _variant: this.state.variant,
      };
      // Their own portrait on the cart line (the cart shows it instead of the style's example),
      // and which version, in words, for checkout and the order.
      const chosen = this.state.variants.find((v) => v.variant === this.state.variant);
      const index = this.state.variants.indexOf(chosen);
      if (this.state.variants.length > 1 && index >= 0) properties[this.t('cart_version_label')] = this.t('cart_version_value', { n: index + 1, total: this.state.variants.length });
      if (chosen?.cart_image_url) properties._preview_image = chosen.cart_image_url;
      if (note) Object.assign(properties, { 'Gift note': note, _is_gift: 'true' });
      // How the in-box letter begins and is signed (optional; the order page shows them).
      const greeting = this.$('[data-letter-greeting]')?.value.trim().slice(0, 80);
      const signed = this.$('[data-letter-signed]')?.value.trim().slice(0, 80);
      if (greeting) properties['Letter begins'] = greeting;
      if (signed) properties.Signed = signed;
      if (this.recorder) {
        const stopped = this.recorderStopped;
        this.stopRecording();
        await stopped;
      }

      const button = this.$('[data-action="add-to-cart"]');
      const label = button.textContent;
      this.busy(true);
      button.textContent = this.t('adding');
      try {
        if (this.state.voice) {
          try {
            properties._audio_id = await this.uploadVoice();
            properties._is_gift = 'true';
          } catch (err) {
            if (err?.name === 'AbortError') throw err;
            throw new Error('voice_upload');
          }
        }
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1, properties }] }),
        });
        if (!response.ok) throw new Error('cart');
        window.location.assign(this.cartUrl);
      } catch (err) {
        button.textContent = label;
        this.busy(false);
        this.status(this.error(err), 'error');
      }
    }

    // ------------------------------------------------------------------ http

    async request(path, { method = 'GET', body, auth = true } = {}) {
      const headers = {};
      if (body) headers['Content-Type'] = 'application/json';
      if (auth && this.state.token) headers.Authorization = `Bearer ${this.state.token}`;
      const response = await fetch(this.api + path, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: this.abort.signal,
      });
      let data = {};
      try {
        data = await response.json();
      } catch {}
      if (!response.ok) throw new ApiError(data.error || 'generic', response.status, data);
      return data;
    }
  }

  if (!customElements.get('yugal-studio')) customElements.define('yugal-studio', YugalStudio);
})();
